import { AlertCircle } from "lucide-react";
import {
  DryRunErrorRows,
  DryRunWarningRows,
} from "@/features/people/components/dry-run-error-rows";
import {
  describeCreatedLookups,
  describeExtensionCounts,
  warningRowCount,
} from "@/features/people/lib/dry-run-summary";
import type { DryRunResult } from "@/features/people/schemas/import.schema";
import { Badge } from "@/shared/components/ui/badge";

/// What an import will do, read from its dry run: row counts, what the extra
/// handlers will write, new departments, reporting cycles, and every row that
/// will be skipped with the reason, or imported with some fields skipped. Never renders `rawInput`: a row can carry
/// CTC and insurance numbers, and the preview does not need them.
export function DryRunReport({ dry }: { dry: DryRunResult }) {
  const extensions = describeExtensionCounts(dry.extensionCounts);
  const warned = warningRowCount(dry);
  const createdLookups = describeCreatedLookups(dry.extensionCounts);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{dry.classifications.new} new</Badge>
        <Badge variant="secondary">{dry.classifications.update_existing} to update</Badge>
        {dry.classifications.skip_no_changes > 0 ? (
          <Badge variant="outline">{dry.classifications.skip_no_changes} unchanged</Badge>
        ) : null}
        {warned > 0 ? <Badge variant="outline">{warned} with skipped fields</Badge> : null}
        {dry.classifications.error > 0 ? (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="size-3" aria-hidden="true" />
            {dry.classifications.error} with errors
          </Badge>
        ) : null}
      </div>

      {extensions.length > 0 ? (
        <dl className="flex flex-col gap-1 text-xs">
          {extensions.map((line) => (
            <div key={line.key} className="flex flex-wrap gap-x-2">
              <dt className="font-medium text-text-primary">{line.label}:</dt>
              <dd className="text-text-secondary">
                {line.counts.map((count) => `${count.value} ${count.label}`).join(", ")}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {dry.newDepartmentsToCreate.length > 0 ? (
        <p className="text-xs text-text-secondary">
          New department{dry.newDepartmentsToCreate.length === 1 ? "" : "s"} to create:{" "}
          <span className="font-medium text-text-primary">
            {dry.newDepartmentsToCreate.join(", ")}
          </span>
        </p>
      ) : null}

      {createdLookups.map((line) => (
        <p key={line} className="text-xs text-text-secondary">
          {line}
        </p>
      ))}

      {dry.cycleWarnings.length > 0 ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <p className="font-medium">
            {dry.cycleWarnings.length} circular reporting chain
            {dry.cycleWarnings.length === 1 ? "" : "s"} in this file:
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {dry.cycleWarnings.map((warning) => (
              <li key={warning.via.join(">")} className="font-mono">
                {warning.via.join(" -> ")}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <DryRunErrorRows dry={dry} />
      <DryRunWarningRows dry={dry} />
    </div>
  );
}
